<template>
	<div>
		<button
			class="button"
			:class="classObject"
			@click="onClick"
		>
			<slot />
		</button>
	</div>
</template>

<script>
export default {
	name: 'AppButton',
	props: {
		size: {
			type: String,
			required: false,
			default: '',
		},
		color: {
			type: String,
			required: false,
			default: '',
		},
		item: {
			type: String,
			required: false,
			default: '',
		},
		gap: {
			type: String,
			required: false,
			default: '',
		},
		loading: {
			type: Boolean,
			required: false,
			default: false,
		},
		success: {
			type: Boolean,
			required: false,
			default: false,
		},
		disabled: {
			type: Boolean,
			required: false,
			default: false,
		},
	},

	data() {
		return {
			clicked: false,
		};
	},

	computed: {
		classObject() {
			return {
				'is-flex-flex': this.size === 'flex-flex',
				'is-flex': this.size === 'flex',
				'is-small': this.size === 'small',
				'is-small-defaultheight': this.size === 'small-defaultheight',
				'is-medium': this.size === 'medium',
				'is-large': this.size === 'large',
				'is-fullwidth': this.size === 'full',
				'is-primary': this.color === 'primary',
				'is-secondary': this.color === 'secondary',
				'is-grey': this.color === 'grey',
				'is-transparent': this.color === 'none',
				'is-error': this.color === 'error',
				'has-outline': this.color === 'outline',
				'has-outline-reverse': this.color === 'outline-reverse',
				'has-outline-light': this.color === 'outline-light',
				'has-outline-dark': this.color === 'outline-dark',
				'has-outline-red': this.color === 'outline-red',
				'has-icon': this.item === 'icon',
				'gap-left': this.gap === 'left',
				'gap-right': this.gap === 'right',
				'is-disabled': this.disabled,
				'is-loading': this.loading,
				'is-clicked': this.clicked,
				'is-success': this.success,
			};
		},
	},

	methods: {
		onClick($event) {
			if (!this.disabled && !this.success) {
				this.clicked = true;

				if (!this.loading) {
					this.$emit('click', $event);
				}
			}
		},
	},
};
</script>

<style lang="scss" scoped>
button {
	border-radius: 8px;
	height: 48px;
	font-size: $size-7;
	font-weight: $fontWeight-semibold;
	color: $color-white;
	text-decoration: none;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: 475ms;
	cursor: pointer;
	background-color: transparent;

	&.is-flex {
		padding: 0 $gap-15;
		max-height: 32px;
	}

	&.is-flex-flex {
		padding: 6px 8px;
		height: auto;
	}

	&.is-small {
		min-width: 140px;
		height: 38px;
	}

	&.is-small-defaultheight {
		min-width: 486px;
		height: 38px;
	}

	&.is-medium {
		min-width: 176px;
	}

	&.is-large {
		width: 486px;
	}

	&.is-fullwidth {
		width: 100%;
	}

	&.gap-left {
		margin-left: $gap-5;
	}

	&.gap-right {
		margin-right: $gap-2;
	}

	&.has-icon {
		color: $color-accent-200;
		font-weight: $fontWeight-medium;

		img {
			margin-right: $gap-1;
		}
	}

	&.has-outline {
		border: 1px solid $color-accent-300;
		color: $color-accent-400;
		font-weight: $fontWeight-medium;

		&:hover {
			background-color: $color-accent-300;
		}

		&.is-disabled {
			border: 1px solid $color-accent-400;
		}
	}

	&.has-outline-reverse {
		border: 1px solid $color-accent-400;
		color: $color-accent-300;

		&:hover {
			border: 1px solid $color-accent-300;
			background-color: $color-accent-500;
		}
	}

	&.has-outline-light {
		border: 1px solid $color-accent-300;
		color: $color-accent-300;

		&:hover {
			border: 1px solid $color-accent-300;
			background-color: $color-accent-500;
		}

		&.is-disabled {
			border: 1px solid $color-accent-400;
			color: $color-accent-400;
		}
	}

	&.has-outline-dark {
		border: 1px solid $color-accent-400;
		color: $color-accent-400;

		&:hover {
			border: 1px solid $color-accent-300;
			background-color: $color-accent-300;
		}
	}

	&.has-outline-red {
		border: 1px solid $color-error;
		color: $color-error;

		&:hover {
			border: 1px solid $color-error;
			background-color: $color-accent-500;
		}
	}

	&.is-primary {
		background-color: $color-primary-200;
		border: none;

		&:hover {
			background-color: $color-primary-100;
			border-color: $color-primary-100;
		}

		&.is-disabled {
			background-color: rgba($color: $color-primary-200, $alpha: 0.3);
			border: none;
			cursor: not-allowed;
		}
	}

	&.is-secondary {
		background-color: $color-secondary-200;
		color: $color-black;
		border: none;

		&:hover {
			background-color: $color-secondary-100;
			border-color: $color-secondary-100;
		}

		&.is-disabled {
			background-color: rgba($color: $color-secondary-200, $alpha: 0.3);
			border: none;
			cursor: not-allowed;
		}
	}

	&.is-grey {
		background-color: $color-accent-500;
		color: $color-white;
		border: none;

		&:hover {
			background-color: $color-accent-500;
			border-color: $color-accent-500;
		}
	}

	&.is-error {
		background-color: $color-error;
		color: $color-white;
		border: none;

		&:hover {
			background-color: $color-error;
			border-color: $color-error;
		}
	}

	&.is-transparent {
		border: none;
	}
}
</style>
